import { instance, SIGN_REQUEST_TIMEOUT } from '@shared/api/config';
import { Act, ActDoc, CombinedAct, CombinedActStateCodes } from '@shared/types/osi/acts';
import { CORE_PATH, DOC_PATH } from '@shared/api/paths';

const MAIN_PATH = 'Acts';

const path = `${CORE_PATH}/${MAIN_PATH}`;

/**
 * OSI-763: печатная форма акта. Один и тот же эндпоинт отдаёт и форму на подпись
 * (акт не подписан — чистый PDF), и форму подписанного акта (PDF с блоком расшифровки
 * ЭЦП). Ветвление — на бэкенде, по факту наличия подписей.
 */
export interface WorkCompletionActForm {
  pdfBase64: string;
}

export const getSignedActsByOsiId = async (osiId: number): Promise<Act[]> =>
  instance.get(`${CORE_PATH}/Osi/${osiId}/signed-acts`);

export const getNotSignedActsByOsiId = async (osiId: number): Promise<Act[]> =>
  instance.get(`${CORE_PATH}/Osi/${osiId}/not-signed-acts`);

export const getDocsByActId = async (actId: number): Promise<ActDoc[]> => instance.get(`${path}/${actId}/docs`);

// TODO: add to shared api slice
export const getBase64ByScanId = async (id: number): Promise<string> => instance.get(`${CORE_PATH}/Scans/${id}`);

export const getWorkCompletionAct = async (actId: number): Promise<WorkCompletionActForm> =>
  instance.get(`${DOC_PATH}/act/work-completion/${actId}`);

/**
 * OSI-763: сохранение подписанного акта — единственная фаза, подписывается чистый PDF.
 * extension=cms вместо прежнего pdf: в скан физически кладётся CMS-контейнер
 * (attached-подпись), и расширение файла должно отражать его реальное содержимое.
 * Увеличенный таймаут `SIGN_REQUEST_TIMEOUT` — oci-docs ставит подпись платформы с меткой TSA НУЦ.
 */
export const saveSignedWorkCompletionAct = async (
  actId: number,
  data: string
): Promise<{
  pdfBase64: string;
}> =>
  instance.put(`${DOC_PATH}/act/work-completion/${actId}/sign?extension=cms`, data, {
    timeout: SIGN_REQUEST_TIMEOUT
  });

/**
 * OSI-818: таймаут для операций с общим актом. PDF общего акта собирается из актов
 * всех домов группы, и на крупных БИН/ИИН это заметно дольше стандартных 60 с
 * (таймаут по умолчанию в `shared/api/config.ts`).
 */
const COMBINED_ACT_TIMEOUT = 180_000;

/**
 * OSI-818: общие акты по БИН/ИИН, в состав которых входит акт дома.
 * Без `state` бэкенд отдаёт и CREATED, и SIGNED (ANNULLED не отдаёт никогда).
 */
export const getCombinedActsByOsiId = async (osiId: number, state?: CombinedActStateCodes): Promise<CombinedAct[]> =>
  instance.get(`${CORE_PATH}/Osi/${osiId}/combined-acts`, { params: state ? { state } : undefined });

/** OSI-818: общий акт по id — запасной путь, если в загруженном списке его не оказалось. */
export const getCombinedActById = async (id: number): Promise<CombinedAct> =>
  instance.get(`${CORE_PATH}/CombinedActs/${id}`);

/** OSI-818: PDF неподписанного общего акта (на подпись). Формируется долго — увеличенный таймаут. */
export const getCombinedActForm = async (id: number): Promise<WorkCompletionActForm> =>
  instance.get(`${DOC_PATH}/act/combined/${id}`, { timeout: COMBINED_ACT_TIMEOUT });

/**
 * OSI-818: сохранение подписанного общего акта. Контракт как у `saveSignedWorkCompletionAct`:
 * тело — строка CMS base64, extension=cms, формат ответа и ошибок тот же.
 */
export const saveSignedCombinedAct = async (
  id: number,
  data: string
): Promise<{
  pdfBase64: string;
}> => instance.put(`${DOC_PATH}/act/combined/${id}/sign?extension=cms`, data, { timeout: COMBINED_ACT_TIMEOUT });
